"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const benefits = [
{
  icon: "UsersIcon",
  title: "Connect with professionals",
  desc: "Find and connect with verified vets, groomers, and trainers. Share your pet's profile with one tap.",
  color: "#5EBFB3",
  bg: "#E8F7F6"
},
{
  icon: "DocumentTextIcon",
  title: "Complete health records",
  desc: "Vaccines, allergies, medications, vet visits — all in one searchable, shareable profile.",
  color: "#7C3AED",
  bg: "#EDE9FE"
},
{
  icon: "BellAlertIcon",
  title: "Smart reminders",
  desc: "Never miss a vaccine, flea treatment, or vet appointment. Reminders sent to your phone.",
  color: "#0369A1",
  bg: "#E0F2FE"
},
{
  icon: "ShieldCheckIcon",
  title: "Trusted circle sharing",
  desc: "Share your pet's profile with family, sitters, or vets in one tap. You control who sees what.",
  color: "#D97706",
  bg: "#FEF3C7"
}];


export default function OwnerBenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 dark:bg-[#0f172a]" style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Image + overlay cards */}
          <div className="relative scroll-fade-left">
            <div className="relative rounded-3xl overflow-hidden" style={{ boxShadow: "0 24px 64px rgba(14,165,160,0.18)" }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_15d263364-1769278101290.png"
                alt="Young woman sitting in a park with her dogfob, using DogFob app on her phone to track pet health"
                width={600}
                height={480}
                className="w-full h-[420px] object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2027]/40 to-transparent" />
            </div>

            {/* Floating stat card */}
            <div
              className="absolute -bottom-6 -right-4 sm:-right-8 glass-card dark:bg-[#1e293b] dark:border-slate-700 rounded-2xl p-4 animate-float-a"
              style={{ boxShadow: "0 16px 48px rgba(15,32,39,0.12)" }}>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E0F7F6] dark:bg-teal-900/40 flex items-center justify-center">
                  <Icon name="ShieldCheckIcon" size={20} className="text-teal-500" />
                </div>
                <div>
                  <p className="text-xs text-[#8AA4A4] dark:text-slate-500 font-medium">Pets kept safe</p>
                  <p className="text-2xl font-extrabold text-[#1A2B2B] dark:text-slate-100 font-heading leading-none">94M+</p>
                  <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">US households with pets</p>
                </div>
              </div>
            </div>

            {/* Top floating badge */}
            <div
              className="absolute -top-4 -left-4 glass-card dark:bg-[#1e293b] dark:border-slate-700 rounded-xl px-3 py-2 animate-float-c"
              style={{ boxShadow: "0 8px 24px rgba(15,32,39,0.1)" }}>
              
              <div className="flex items-center gap-2">
                <span className="text-lg">🐾</span>
                <div>
                  <p className="text-[11px] font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">All pets welcome</p>
                  <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">DogFobs, cats & more</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Benefits */}
          <div className="flex flex-col gap-6">
            <div className="scroll-fade-right">
              <span className="teal-badge mb-3">For Pet Owners</span>
              <h2 className="text-4xl sm:text-[42px] font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight leading-tight mt-4 mb-4">
                Everything your pet needs,{" "}
                <span className="gradient-text">nothing they don't.</span>
              </h2>
              <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed">
                From their first vaccine to their daily walk, DogFob keeps every detail organized and every important person informed.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              {benefits.map((b, idx) =>
              <div
                key={b.title}
                className={`scroll-fade-up stagger-${idx + 1} feature-card p-5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}>
                
                  <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: b.bg }}>
                  
                    <Icon name={b.icon as "MapPinIcon"} size={18} style={{ color: b.color }} />
                  </div>
                  <h3 className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 mb-1.5 font-heading">{b.title}</h3>
                  <p className="text-xs text-[#4A6363] dark:text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}